/**
 * Tenant settings configuration
 * MEDIUM-006: Exported for type-safe configuration access
 */
export interface TenantSettings {
  theme?: 'light' | 'dark' | 'auto';

  language?: string;
  timezone?: string;

  dateFormat?: string;
}

/**
 * Comprehensive tenant status options for the multi-tenant dashboard (DEV-190)
 */
export type TenantStatus =
  | 'active'           // Fully operational
  | 'suspended'        // Temporarily disabled (e.g., payment issues)
  | 'pending_approval' // Awaiting admin approval
  | 'trial'            // In trial period
  | 'expired'          // Trial/subscription expired
  | 'deactivated'      // Permanently disabled
  | 'inactive';

  // Legacy status for backwards compatibility

/**
 * Tenant type classification for filtering and billing (DEV-190)
 */
export type TenantType =
  | 'enterprise'  // Large organizations with dedicated resources
  | 'professional' // Mid-size with enhanced features
  | 'standard'    // Standard feature set
  | 'trial'       // Evaluation period
  | 'internal';

  // Internal/testing tenants

/**
 * Tenant status metadata for UI display
 */
export interface TenantStatusInfo {
  status: TenantStatus;

  label: string;
  description: string;

  severity: 'success' | 'warning' | 'danger' | 'info';
  icon: string;
}

/**
 * Tenant type metadata for UI display
 */
export interface TenantTypeInfo {
  type: TenantType;

  label: string;
  description: string;

  color: string;
  icon: string;
}

/**
 * Status metadata lookup table
 */
export const TENANT_STATUS_INFO: Record<TenantStatus, TenantStatusInfo> = {
  active: {
    status: 'active',
    label: 'Active',
    description: 'Tenant is fully operational',
    severity: 'success',
    icon: 'pi pi-check-circle'
  },
  suspended: {
    status: 'suspended',
    label: 'Suspended',
    description: 'Tenant temporarily disabled',
    severity: 'warning',
    icon: 'pi pi-pause-circle'
  },
  pending_approval: {
    status: 'pending_approval',
    label: 'Pending Approval',
    description: 'Awaiting admin approval',
    severity: 'info',
    icon: 'pi pi-clock'
  },
  trial: {
    status: 'trial',
    label: 'Trial',
    description: 'In trial period',
    severity: 'info',
    icon: 'pi pi-star'
  },
  expired: {
    status: 'expired',
    label: 'Expired',
    description: 'Trial or subscription expired',
    severity: 'danger',
    icon: 'pi pi-exclamation-triangle'
  },
  deactivated: {
    status: 'deactivated',
    label: 'Deactivated',
    description: 'Permanently disabled',
    severity: 'danger',
    icon: 'pi pi-ban'
  },
  inactive: {
    status: 'inactive',
    label: 'Inactive',
    description: 'Legacy inactive status',
    severity: 'warning',
    icon: 'pi pi-minus-circle'
  }
};

/**
 * Type metadata lookup table
 */
export const TENANT_TYPE_INFO: Record<TenantType, TenantTypeInfo> = {
  enterprise: {
    type: 'enterprise',
    label: 'Enterprise',
    description: 'Large organizations with dedicated resources',
    color: '#6366F1', // Indigo
    icon: 'pi pi-building'
  },
  professional: {
    type: 'professional',
    label: 'Professional',
    description: 'Mid-size with enhanced features',
    color: '#8B5CF6', // Purple
    icon: 'pi pi-briefcase'
  },
  standard: {
    type: 'standard',
    label: 'Standard',
    description: 'Standard feature set',
    color: '#06B6D4', // Cyan
    icon: 'pi pi-users'
  },
  trial: {
    type: 'trial',
    label: 'Trial',
    description: 'Evaluation period',
    color: '#F59E0B', // Amber
    icon: 'pi pi-clock'
  },
  internal: {
    type: 'internal',
    label: 'Internal',
    description: 'Internal/testing tenants',
    color: '#64748B', // Slate
    icon: 'pi pi-cog'
  }
};

export interface Tenant {
  id: string;

  name: string;
  code: string;

  logo?: string;
  domain?: string;

  status: TenantStatus;
  /** Tenant type classification for filtering and billing (DEV-190) */
  tenantType?: TenantType;

  features: string[];
  settings?: TenantSettings;

  createdAt: Date;
  updatedAt: Date;

  /** Client-side tracking for recent tenants feature */
  lastAccessed?: Date;

  /** User's role within this tenant (e.g., 'admin', 'trader', 'viewer') */
  userRole?: string;

  /** Trial expiration date if applicable */
  trialExpiresAt?: Date;

  /** Number of users in this tenant */
  userCount?: number;

  /** Contact email for tenant admin */
  contactEmail?: string;
}

export interface TenantContext {
  currentTenant: Tenant | null;

  availableTenants: Tenant[];
  isLoading: boolean;

  error: string | null;
}

export interface SwitchTenantRequest {
  tenant_id: string;  // Backend expects snake_case
  reason?: string;
}

export interface SwitchTenantResponse {
  success: boolean;

  tenant: Tenant;
  token?: string;

  permissions?: string[];
}

/**
 * Tenant filter criteria for dashboard (DEV-190)
 */
export interface TenantFilter {
  /** Filter by status(es) */
  statuses?: TenantStatus[];

  /** Filter by type(s) */
  types?: TenantType[];

  /** Search by name or code */
  searchTerm?: string;

  /** Filter by feature flag presence */
  hasFeature?: string;

  /** Show only tenants expiring within N days */
  expiringWithinDays?: number;
}

/**
 * Tenant statistics for dashboard cards (DEV-190)
 */
export interface TenantStats {
  /** Total number of tenants */
  total: number;

  /** Counts by status */
  byStatus: Record<TenantStatus, number>;

  /** Counts by type */
  byType: Record<TenantType, number>;

  /** Tenants with trials expiring soon (within 7 days) */
  expiringTrials: number;

  /** Tenants pending approval */
  pendingApproval: number;

  /** Tenants added in the last 30 days */
  newThisMonth: number;

  /** Total user count across all tenants */
  totalUsers: number;
}

/**
 * Helper functions for tenant model operations
 */
export function getStatusInfo(status: TenantStatus): TenantStatusInfo {
  return TENANT_STATUS_INFO[status];
}

export function getTypeInfo(type: TenantType): TenantTypeInfo {
  return TENANT_TYPE_INFO[type];
}

/**
 * Check if a tenant matches filter criteria
 */
export function matchesTenantFilter(tenant: Tenant, filter: TenantFilter): boolean {
  // Status filter
  if (filter.statuses?.length && !filter.statuses.includes(tenant.status)) {
    return false;

    }

  // Type filter
  if (filter.types?.length && tenant.tenantType && !filter.types.includes(tenant.tenantType)) {
    return false;

    }

  // Search term filter
  if (filter.searchTerm) {
    const searchLower = filter.searchTerm.toLowerCase();

    const matchesName = tenant.name.toLowerCase().includes(searchLower);
    const matchesCode = tenant.code.toLowerCase().includes(searchLower);

    const matchesDomain = tenant.domain?.toLowerCase().includes(searchLower) ?? false;
    if (!matchesName && !matchesCode && !matchesDomain) {
      return false;

      }
  }

  // Feature filter
  if (filter.hasFeature && !tenant.features.includes(filter.hasFeature)) {
    return false;

    }

  // Expiring filter
  if (filter.expiringWithinDays !== undefined && tenant.trialExpiresAt) {
    const expiryDate = new Date(tenant.trialExpiresAt);

    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() + filter.expiringWithinDays);

    if (expiryDate > cutoffDate) {
      return false;

      }
  }

  return true;
}

/**
 * Calculate tenant statistics from a list of tenants
 */
export function calculateTenantStats(tenants: Tenant[]): TenantStats {
  const now = new Date();

  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

  const byStatus: Record<TenantStatus, number> = {
    active: 0,
    suspended: 0,
    pending_approval: 0,
    trial: 0,
    expired: 0,
    deactivated: 0,
    inactive: 0
  };

  const byType: Record<TenantType, number> = {
    enterprise: 0,
    professional: 0,
    standard: 0,
    trial: 0,
    internal: 0
  };

  let expiringTrials = 0;
  let pendingApproval = 0;

  let newThisMonth = 0;
  let totalUsers = 0;

  for (const tenant of tenants) {
    // Count by status
    byStatus[tenant.status]++;

    // Count by type
    if (tenant.tenantType) {
      byType[tenant.tenantType]++;

      }

    // Expiring trials
    if (tenant.status === 'trial' && tenant.trialExpiresAt) {
      const expiryDate = new Date(tenant.trialExpiresAt);

      if (expiryDate <= sevenDaysFromNow && expiryDate > now) {
        expiringTrials++;

        }
    }

    // Pending approval
    if (tenant.status === 'pending_approval') {
      pendingApproval++;

      }

    // New this month
    if (tenant.createdAt && new Date(tenant.createdAt) >= thirtyDaysAgo) {
      newThisMonth++;

      }

    // Total users. Guard explicitly against undefined so a future refactor
    // that adds `count++` inside this branch still counts tenants with 0 users.
    if (tenant.userCount !== undefined) {
      totalUsers += tenant.userCount;

      }
  }

  return {
    total: tenants.length,
    byStatus,
    byType,
    expiringTrials,
    pendingApproval,
    newThisMonth,
    totalUsers
  };
}
